import { useRouter } from 'next/router';

import { Category, ProductPreview, ProductSetPreview } from 'domain/types';
import { CategoriesRepo, ProductPreviewsRepo, SetsRepo } from 'domain/repositories';
import Layout from 'components/layout/Layout';
import CategoryNavigation from 'components/shop/CategoryNavigation/CategoryNavigation';
import ProductTiles from 'components/shop/ProductTiles/ProductTiles';
import { flatProductPreviewsWithColors } from 'utils/helpers/products/flatProductPreviewsWithColors';
import { setsToProductPreviews } from 'utils/helpers/sets/setsToProductPreviews';
import { sortByAvailability } from 'utils/helpers/products/sortByAvailability';
import { REVALIDATES } from 'domain/constants';
import PageLoader from 'components/ui/loaders/PageLoader';

interface CategoryPageProps {
  categories: Array<Category>;
  products: Array<ProductPreview>;
  category: Category;
}

const CategoryPage: React.FC<CategoryPageProps> = ({
  categories,
  products,
  category
}): JSX.Element => {
  const router = useRouter();

  if (router.isFallback) return <PageLoader />;

  return (
    <Layout
      pageMeta={{
        title: category.metaTitle || category.name,
        description: category.metaDescription || category.description,
        imageUrl: category.image.url
      }}
    >
      <CategoryNavigation categories={categories} categorySlug={category.slug} />

      <ProductTiles products={products} />
    </Layout>
  );
};

export const getStaticPaths = async () => {
  const slugs = await CategoriesRepo.getSlugs();

  const paths = slugs.map(({ slug }) => ({ params: { category: slug } }));

  return {
    paths,
    fallback: true
  };
};

interface CategoryPageContext {
  params: { category: string };
}

export const getStaticProps = async (ctx: CategoryPageContext) => {
  try {
    const {
      params: { category: categorySlug }
    } = ctx;

    const [categories, productsWithColorCollection, setPreviews] = await Promise.all([
      CategoriesRepo.get(),
      ProductPreviewsRepo.getProductsByCategorySlug(categorySlug),
      SetsRepo.getPreviewsByCategorySlug(categorySlug).catch(() => [] as ProductSetPreview[])
    ]);

    const products = sortByAvailability([
      ...flatProductPreviewsWithColors(productsWithColorCollection),
      ...setsToProductPreviews(setPreviews)
    ]);

    const category = categories.find(({ slug }) => slug === categorySlug);

    if (!category)
      return {
        notFound: true,
        revalidate: REVALIDATES.category
      };

    return {
      props: {
        categories,
        products,
        category
      },
      revalidate: REVALIDATES.category
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(error);

    return {
      notFound: true,
      revalidate: REVALIDATES.category
    };
  }
};

export default CategoryPage;
